import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bell, Plus, Radio } from "lucide-react";
import { toast } from "sonner";

const statusStyles = {
  Concept: "text-white/60 border-white/20",
  "In sviluppo": "text-accent border-accent/50",
  Alpha: "text-accent border-accent/50",
  "Pre-Order Soon": "text-black bg-accent border-accent",
};

export default function GameDetailPage({ data }) {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const game = data.releases.find((r) => r.id === id);
  if (!game) return <Navigate to="/games" replace />;

  const currentIndex = data.releases.findIndex((r) => r.id === id);
  const next = data.releases[(currentIndex + 1) % data.releases.length];

  const notify = () => {
    toast.success(`You'll be notified about ${game.title}`, {
      description: "Priority updates enabled.",
    });
  };

  return (
    <div data-testid={`game-detail-${game.id}`} className="bg-black">
      {/* Hero */}
      <section className="relative min-h-[92svh] w-full overflow-hidden">
        <motion.div
          initial={{ scale: 1.1, opacity: 0.4 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={game.cover}
            alt={game.title}
            className="w-full h-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/40" />

        <div className="relative z-10 min-h-[92svh] flex flex-col justify-end px-6 md:px-12 lg:px-16 pt-32 pb-16 md:pb-20 max-w-[1600px] mx-auto w-full">
          <Link
            to="/games"
            data-testid="back-to-games"
            className="group inline-flex items-center gap-2 text-white/60 hover:text-accent transition-colors mb-10 self-start"
          >
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            <span className="font-body text-[10px] uppercase tracking-[0.3em]">
              Tutti i giochi
            </span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="flex flex-wrap items-center gap-3 mb-8"
          >
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
              {game.codename} — {game.genre}
            </span>
            <span className="w-8 h-px bg-white/30" />
            <span
              className={`font-body text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border backdrop-blur-sm ${statusStyles[game.status] || "text-white/60 border-white/20"}`}
            >
              {game.status}
            </span>
            {game.platforms.map((p) => (
              <span
                key={p}
                className="font-body text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 border border-white/20 text-white/80 backdrop-blur-sm bg-white/[0.03]"
              >
                {p}
              </span>
            ))}
          </motion.div>

          <motion.h1
            data-testid="game-detail-title"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading uppercase text-white text-6xl md:text-8xl lg:text-[10rem] leading-[0.85] tracking-normal mb-6 max-w-[16ch]"
          >
            {game.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9 }}
            className="font-heading text-2xl md:text-3xl lg:text-4xl text-white/80 max-w-3xl uppercase leading-tight mb-10"
          >
            {game.hero.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <button
              type="button"
              data-testid="game-trailer-btn"
              onClick={(e) => e.preventDefault()}
              className="group inline-flex items-center gap-4 border border-accent/40 bg-accent/[0.06] backdrop-blur-sm px-6 py-4 cursor-default"
            >
              <span className="relative flex items-center justify-center w-8 h-8 rounded-full border border-accent/40">
                <Radio size={13} className="text-accent" />
                <span className="absolute inset-0 rounded-full bg-accent/20 animate-ping" />
              </span>
              <span className="font-heading text-lg tracking-[0.15em] uppercase text-accent">Trailer in arrivo</span>
            </button>
            <button
              data-testid="game-notify-btn"
              onClick={notify}
              className="group inline-flex items-center justify-center gap-3 border border-white/25 text-white px-6 py-4 hover:border-accent hover:text-accent transition-colors"
            >
              {game.status === "Pre-Order Soon" ? <Plus size={16} /> : <Bell size={14} />}
              <span className="font-heading text-lg tracking-[0.15em] uppercase">
                {game.status === "Pre-Order Soon" ? "Avvisa al pre-order" : "Avvisami"}
              </span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Synopsis + meta */}
      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                Synopsis
              </span>
            </div>
            <p className="font-heading uppercase text-white/90 text-3xl md:text-4xl lg:text-5xl leading-[1.05] mb-10">
              {game.hero.quote}
            </p>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-2xl">
              {game.hero.synopsis}
            </p>
          </motion.div>
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-4 border border-white/10 p-8 md:p-10 self-start bg-[#09090B]"
          >
            <div className="font-body text-[10px] uppercase tracking-[0.35em] text-accent mb-6">
              Scheda di produzione
            </div>
            <dl className="space-y-5">
              <MetaRow label="Nome in codice" value={game.codename} />
              <MetaRow label="Uscita" value={game.window} />
              <MetaRow label="Stato" value={game.status} />
              <MetaRow label="Genere" value={game.genre} />
              <MetaRow label="Regia" value={game.director} />
              <MetaRow label="Piattaforme" value={game.platforms.join(" / ")} />
            </dl>
          </motion.aside>
        </div>
      </section>

      {/* Feature bullets */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="font-heading uppercase text-white text-4xl md:text-6xl mb-10 md:mb-14"
          >
            Sistemi distintivi
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {game.hero.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                data-testid={`game-feature-${i}`}
                className="group flex items-start gap-6 border border-white/10 bg-[#09090B] p-6 md:p-8 hover:border-white/30 transition-colors duration-500"
              >
                <span className="font-heading text-3xl md:text-4xl text-white/15 group-hover:text-accent/60 transition-colors duration-500 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-body text-base md:text-lg text-white/80 leading-relaxed">
                  {f}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-12 h-px bg-accent" />
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
              Galleria
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 md:gap-4">
            {game.gallery.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className={`relative overflow-hidden border border-white/10 group ${
                  i === 0 ? "md:col-span-4 aspect-[16/9]" : "md:col-span-2 aspect-[4/3]"
                }`}
              >
                <img
                  src={src}
                  alt={`${game.title} still ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next up */}
      {next && (
      <section className="px-6 md:px-12 lg:px-16 pb-32 md:pb-40 border-t border-white/10 pt-24 md:pt-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="font-body text-[10px] uppercase tracking-[0.35em] text-white/40 mb-4">
            Prossimo nel catalogo
          </div>
          <Link
            to={`/games/${next.id}`}
            data-testid="next-game-link"
            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-6 border-t border-white/10 hover:bg-white/[0.02] transition-colors"
          >
            <div className="lg:col-span-3 relative overflow-hidden aspect-[4/3]">
              <img
                src={next.cover}
                alt={next.title}
                className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <div className="lg:col-span-7">
              <div className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                {next.codename} — {next.window}
              </div>
              <h3 className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-none group-hover:text-accent transition-colors">
                {next.title}
              </h3>
            </div>
            <div className="lg:col-span-2 flex justify-end font-heading text-2xl uppercase text-white/50 group-hover:text-accent transition-colors">
              Avanti →
            </div>
          </Link>
        </div>
      </section>
      )}
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3">
      <dt className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40">
        {label}
      </dt>
      <dd className="font-heading text-lg uppercase text-white/90 text-right">
        {value}
      </dd>
    </div>
  );
}

